
import { Component, Vue, Prop } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import ForecastSetting from '@/models/forecast_setting';
import { FEATURES } from '@/config/featureConfig';

@Component({
  components: {
  }
})
export default class ForecastingMethodSelector extends Vue {
  @Prop({ type: Object as () => StoreModel, required: true })
  private store!: StoreModel;

  private selectedForecastingMethod: string | null = null;
  private methodStagedForEdit: string | null = null;
  private editMode: boolean = false;
  private isExpanded: boolean = false; // Default to collapsed
  private loadingForecastSettings: boolean = false;
  private forecastSetting: ForecastSetting = new ForecastSetting();
  
  async created() {
    await this.loadStoreForecastSettings();
    this.methodStagedForEdit = this.selectedForecastingMethod;
  }

  private toggleExpansion(): void {
    this.isExpanded = !this.isExpanded;
  }

  private emitActiveMethodDetails(methodValue: string | null) {
    if (methodValue) {
      const methodDetails = this.methodologyOptions.find(m => m.value === methodValue);
      if (methodDetails) {
        this.$emit('active-method-details', { 
          value: methodDetails.value,
          text: methodDetails.text,
          description: methodDetails.description 
        });
      } else {
        // Fallback for unknown method value, though ideally options should be exhaustive
        this.$emit('active-method-details', { value: methodValue, text: methodValue, description: 'Details not found for this method.' });
      }
    } else {
      this.$emit('active-method-details', { value: null, text: 'Not Set', description: 'No method selected or loaded.'});
    }
  }

  get currentSelectedMethodNameViewMode(): string {
    const method = this.methodologyOptions.find(m => m.value === this.selectedForecastingMethod);
    return method ? method.text : (this.selectedForecastingMethod || 'Not Set');
  }

  get currentMethodDescriptionViewMode(): string {
    const method = this.methodologyOptions.find(m => m.value === this.selectedForecastingMethod);
    return method ? method.description : '';
  }

  get currentMethodDescriptionEditMode(): string {
    const method = this.methodologyOptions.find(m => m.value === this.methodStagedForEdit);
    return method ? method.description : '';
  }
  
  get methodologyOptions(): Array<{ text: string, value: string, description: string }> {
    const baseOptions = [
      {
        text: 'Simple average moving',
        value: 'simple_average',
        description: 'Uses a straightforward average of historical data. Best for stable data.'
      },
      {
        text: 'Computing model',
        value: 'combined_weighted',
        description: 'Combines moving averages with yearly trends. Ideal for seasonal data.'
      }
    ];

    if (this.store && typeof this.store.isValidateRule === 'function' && this.store.isValidateRule(FEATURES.AI_ML_DEMAND_FORECASTING, 'unlocked')) {
      baseOptions.push({
        text: 'Smart model (using AI)',
        value: 'ai',
        description: 'AI-powered model for complex, dynamic data patterns. Date range not required for suggestion.'
      });
    }
    return baseOptions;
  }

  private enterEditMode(): void {
    this.methodStagedForEdit = this.selectedForecastingMethod;
    this.isExpanded = true; // Ensure expanded when entering edit mode
    this.editMode = true;
  }

  private cancelEditMode(): void {
    this.editMode = false;
    this.isExpanded = false; // Collapse on cancel
    // Reset staged method to current saved method if user cancels
    this.methodStagedForEdit = this.selectedForecastingMethod;
  }

  private async loadStoreForecastSettings(): Promise<void> {
    this.loadingForecastSettings = true;
    try {
      await this.forecastSetting.load();
      if (this.forecastSetting.options && this.forecastSetting.options.methodology) {
        this.selectedForecastingMethod = this.forecastSetting.options.methodology;
      } else {
        // Default to the first option if available, otherwise null
        this.selectedForecastingMethod = this.methodologyOptions.length > 0 ? this.methodologyOptions[0].value : null;
      }
      this.emitActiveMethodDetails(this.selectedForecastingMethod);
    } catch (error) {
      console.error('Failed to load forecast settings:', error);
      this.$emit('error', 'Failed to load forecast settings');
      this.selectedForecastingMethod = this.methodologyOptions.length > 0 ? this.methodologyOptions[0].value : null;
      this.emitActiveMethodDetails(this.selectedForecastingMethod); // Emit default/fallback
    } finally {
      this.loadingForecastSettings = false;
    }
  }

  private async applyStagedChanges(): Promise<void> {
    if (this.methodStagedForEdit === null) {
        this.$emit('error', 'No forecasting method selected.');
        return;
    }
    const currentSavedMethod = this.selectedForecastingMethod;
    if (this.methodStagedForEdit === currentSavedMethod) {
      this.editMode = false; 
      this.isExpanded = false; // Collapse if no changes were made but save was clicked
      return;
    }

    const previousActiveMethod = this.selectedForecastingMethod; // Keep for potential rollback
    
    if (!this.forecastSetting.options) {
      this.forecastSetting.options = { methodology: this.methodStagedForEdit } as any;
    } else {
      this.forecastSetting.options.methodology = this.methodStagedForEdit;
    }

    try {
      this.loadingForecastSettings = true; 
      await this.forecastSetting.save();
      this.selectedForecastingMethod = this.methodStagedForEdit; // Commit the change
      this.$emit('success', 'Forecasting method updated successfully.');
      this.$emit('method-applied'); 
      this.emitActiveMethodDetails(this.selectedForecastingMethod);
      this.editMode = false;
      this.isExpanded = false; // Collapse after successful save
    } catch (error) {
      console.error('Failed to save forecast method:', error);
      // Rollback optimistic options change on error
      if (this.forecastSetting.options) {
        this.forecastSetting.options.methodology = previousActiveMethod; 
      }
      this.$emit('error', 'Failed to save forecasting method.');
      // Keep expanded and in edit mode on error for user to correct, selectedForecastingMethod remains unchanged here
    } finally {
        this.loadingForecastSettings = false;
    }
  }

  private navigateToForecastSettings(): void {
    this.$emit('navigate-to-settings');
  }
}
